import { FormProvider, getFormProps } from '@conform-to/react'
import { Badge, Box, Button, Flex, Strong } from '@radix-ui/themes'
import { useLocation, useOutletContext, useParams } from '@remix-run/react'
import { type TFunction } from 'i18next'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { type Params } from 'react-router-dom'

import { FormDefaults, FormDefaultsHiddenField } from '~/components/common/form-defaults.tsx'
import { StringToComponent } from '~/components/common/string-to-component'
import { SubmissionButton } from '~/components/common/submission-button.tsx'
import { type GetProduct } from '~/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { type ProductOptionValueNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-option-value.ts'
import { type ProductOptionNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-option.ts'
import { type ProductVariantNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-variant.ts'
import { PRODUCTS_SLUG_FORM_ADD_TO_CART, PRODUCTS_SLUG_INPUTS } from '~/routes/($lang).products.$slug/slug.component.tsx'
import { type Context, appView } from '~/routes/_layout/root.component.tsx'
import { useNotify } from '~/utils/hooks/use-notify.ts'
import { useOptimisticAddToCart } from '~/utils/hooks/use-optimistic-add-to-cart.ts'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	uiProductAddToCart = 'ui-product-add-to-cart',
}

/* -------------------------------------------------------------------------------------------------
 * TestIds
 * -----------------------------------------------------------------------------------------------*/

enum productAddToCartTestIds {
	addToCart = 'add-to-cart',
}

/* -------------------------------------------------------------------------------------------------
 * ProductAddToCart
 * -----------------------------------------------------------------------------------------------*/

type ProductAddToCartProps = {
	label: string
	product: GetProduct
	variant: ProductVariantNormalized
	design?: 'default' | 'small'
	t: TFunction<string, undefined>
	baseParams?: Params
}

const ProductAddToCart: React.FC<ProductAddToCartProps> = (props) => {
	const { label, product, variant, t, baseParams } = props

	const { locale, setApp } = useOutletContext<Context>()
	const location = useLocation()
	const params = useParams()
	const { t: tUi } = useTranslation(I18NEXT.uiProductAddToCart)

	const {
    fetcher,
    formValues,
    form,
    fields,
    disabled,
  } = useOptimisticAddToCart({
		product,
		variant,
	})

	useNotify({
		...fetcher,
		setApp,
		translations: {
			success: {
				title: t('notify.addToCart.success.title'),
				description: (
					<StringToComponent
						text={t('notify.addToCart.success.description-$val')}
					>
						<Strong>{product.name}</Strong>
					</StringToComponent>
				),
			},
			error: {
				title: t('notify.addToCart.error.title'),
				description: (
					<StringToComponent
						text={t('notify.addToCart.error.description-$val')}
					>
						<Strong>{product.name}</Strong>
					</StringToComponent>
				),
			},
		},
	})

	return (
		<>
			<Flex justify="center" mt="6">
				{!product.enabled ? (
					<Badge color="red" className="my-2">
						{tUi('notify.noLongerInTheAssortment')}
					</Badge>
				) : !variant.inStock ? (
					<Badge color="red" variant="soft">
						{tUi('notify.soldOut')}
					</Badge>
				) : null}
			</Flex>
			<Flex
				direction="column"
				justify={{ initial: 'center', lg: 'start' }}
				mt="2"
				width="100%"
			>
				<FormProvider context={form.context}>
					<fetcher.Form
						method="post"
						action={`/${locale}${PRODUCTS_SLUG_FORM_ADD_TO_CART.generateAction!(baseParams || params)}${location.search}`}
						{...getFormProps(form)}
						preventScrollReset
					>
						<FormDefaults
              form={form}
              fields={fields}
              values={formValues}
            />
            <FormDefaultsHiddenField meta={fields[PRODUCTS_SLUG_INPUTS.variantId]} />
            <FormDefaultsHiddenField meta={fields[PRODUCTS_SLUG_INPUTS.quantity]} />
            <FormDefaultsHiddenField meta={fields[PRODUCTS_SLUG_INPUTS.slug]} />
            <FormDefaultsHiddenField meta={fields[PRODUCTS_SLUG_INPUTS.variantImage]} />
            <FormDefaultsHiddenField meta={fields[PRODUCTS_SLUG_INPUTS.variantPrice]} />
            <FormDefaultsHiddenField meta={fields[PRODUCTS_SLUG_INPUTS.name]} />
            <FormDefaultsHiddenField meta={fields[PRODUCTS_SLUG_INPUTS.variantOptionsValuesLength]} />
						{(variant.optionValues as ProductOptionValueNormalized[])?.map(
							({ code, value, option }, i) => (
								<Box key={code}>
									<input
										value={(option as ProductOptionNormalized).name!}
										type="hidden"
										name={`variantOptionsValues[${i}][name]`}
									/>
									<input
										value={code}
										type="hidden"
										name={`variantOptionsValues[${i}][code]`}
									/>
									<input
										value={value!}
										type="hidden"
										name={`variantOptionsValues[${i}][value]`}
									/>
								</Box>
							),
						)}
						<Flex direction="column" align="stretch">
							<Button
								type="submit"
								color="green"
								size="4"
								data-testid={productAddToCartTestIds.addToCart}
								disabled={disabled}
								onClick={() =>
									setApp((prev) => ({
										...prev,
										sidebarView: appView.cartView,
										displaySidebar: true,
									}))
								}
							>
								<SubmissionButton fetcher={fetcher}>
                  {label}
								</SubmissionButton>
							</Button>
						</Flex>
					</fetcher.Form>
				</FormProvider>
			</Flex>
		</>
	)
}
ProductAddToCart.displayName = 'ProductAddToCart'

/* -----------------------------------------------------------------------------------------------*/

export {
	ProductAddToCart,
	productAddToCartTestIds,
	I18NEXT as PRODUCT_ADD_TO_CART_I18NEXT,
}
